import { fetchApi } from './api'
import type { DeadLetterMessage, DeadLetterQueueStats } from '../types/queues'

/**
 * Dead-letter queues (`DeadLetterController`, `api/system/dead-letters`).
 *
 * Read-only, like the endpoints. Replay is a manual runbook procedure
 * (docs/operations/dead-letter-runbook.md); there is no endpoint to wrap, and
 * none should be added here without its own authorization decision.
 */

const BASE = '/api/system/dead-letters'

/**
 * Every configured dead-letter queue's depth. Any authenticated caller may read
 * it. A queue that could not be inspected comes back with `unavailable` set,
 * not as a failed request, so one broker hiccup does not blank the list.
 */
export function getDeadLetterStats(): Promise<DeadLetterQueueStats[]> {
  return fetchApi<DeadLetterQueueStats[]>(BASE)
}

/**
 * Peeks up to `max` dead-lettered messages without consuming them.
 *
 * `queue` is a stats row's `sourceConsumer` (the consumer queue or
 * subscription), NOT its `queueName`. A name the server does not have
 * configured is a 400, which `fetchApi` surfaces as an ApiError carrying the
 * message.
 *
 * Needs Lighthouse.Admin: the response carries full message bodies. Outside
 * Development an API-key session gets a 403, so render that as restricted,
 * not as an error to retry.
 *
 * `max` is passed through unchanged. The server clamps it to between 1 and
 * `DeadLetter:MaxPeekMessages` (50 by default), so the result can be shorter
 * than asked even when more are waiting. On RabbitMQ (Development) there is no
 * native peek: the server gets and requeues each message, so treat the result
 * as a sample of the queue, not an ordered listing of it.
 */
export function peekDeadLetters(queue: string, max = 10): Promise<DeadLetterMessage[]> {
  return fetchApi<DeadLetterMessage[]>(
    `${BASE}/${encodeURIComponent(queue)}/messages?max=${encodeURIComponent(String(max))}`,
  )
}
